// Phase 6.AN — AnalyzeRouteWrapper
// ---------------------------------------------------------------------------
// Forces the Analyze component to FULLY REMOUNT on every ticker change by
// passing a `key={ticker}` prop. Without this wrapper, react-router reuses
// the same Analyze component instance across `/analyze/AAPL → /analyze/MU`
// transitions — which means:
//   * `useState` values persist (stale `notFoundReason="timeout"`)
//   * In-flight axios requests are not cancelled cleanly
//   * Refs continue pointing at the previous attempt
//   * useEffect dependency arrays might race the render cycle
//
// All of those produced the P0 bug class the user reported: a previous
// AAPL timeout would surface as the new MU's "Analysis took too long"
// screen, with no MU request ever firing.
//
// Wrapping with `key={ticker}` is the React-canonical way to say "this
// is a new component, throw the old one away". It's a one-line guarantee
// that no state from the previous attempt can leak into the next one.
//
// This wrapper also emits an `analyze_route_mounted` lifecycle event
// before Analyze itself starts, so the Owner Console can see "the
// /analyze/MU route actually rendered its component" even when Analyze.js
// fails to mount for any reason (auth crash, lazy-load fail, etc.).

import React from "react";
import { useParams } from "react-router-dom";
import Analyze from "@/pages/Analyze";
import { emitLifecycle, newClientTraceId } from "@/lib/lifecycle";

function AnalyzeMountTelemetry({ ticker }) {
  React.useEffect(() => {
    try {
      emitLifecycle("analyze_route_mounted", {
        ticker: (ticker || "").toUpperCase() || null,
        client_trace_id: newClientTraceId(),
        extra: {
          pathname: (typeof window !== "undefined") ? window.location.pathname : null,
          ts: Date.now(),
        },
      });
    } catch { /* swallow */ }
    // Single-fire on mount; the parent `key={ticker}` guarantees we
    // mount fresh for every ticker change, so we don't need
    // dependency-array re-emits here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

export default function AnalyzeRouteWrapper() {
  const { ticker } = useParams();
  // Normalize for the key — this is the canonical identity of THIS
  // analyze attempt. Any change to it forces a full subtree remount.
  const tk = (ticker || "").toUpperCase();
  return (
    <React.Fragment key={tk || "empty"}>
      <AnalyzeMountTelemetry ticker={tk} />
      <Analyze />
    </React.Fragment>
  );
}
